import React, { useState, useEffect } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TextField,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material'
import { firestore } from '../../../../utils/firebase/firebase'
import { toast } from 'react-toastify'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'

const EMPTY_MEMBERSHIP = {
  name: '',
  type: 'individual',
  cycle: 'Weekly',
  price: '',
  stripeAPI: '',
  description: '',
  discountTitle: '',
  order: 0,
  enabled: false,
}

const StyledCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.common.black,
}))

const Memberships = () => {
  const [memberships, setMemberships] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [newMembership, setNewMembership] = useState(EMPTY_MEMBERSHIP)
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    firestore
      .collection('memberships')
      .get()
      .then((snapshot) => {
        const data = []
        snapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() })
        })
        data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        setMemberships(data)
        setLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  const handleNameSave = async (membership) => {
    const trimmed = editName.trim()
    if (!trimmed || trimmed === membership.name) {
      setEditingId(null)
      return
    }
    try {
      await firestore.collection('memberships').doc(membership.id).update({
        name: trimmed,
      })
      setMemberships((prev) =>
        prev.map((m) =>
          m.id === membership.id ? { ...m, name: trimmed } : m
        )
      )
      toast.success(`Renamed to "${trimmed}"`)
    } catch (err) {
      toast.error('Failed to update name')
    }
    setEditingId(null)
  }

  const handleCloseDialog = () => {
    setShowAddForm(false)
    setNewMembership(EMPTY_MEMBERSHIP)
  }

  const handleAddMembership = async () => {
    if (!newMembership.name.trim() || !newMembership.stripeAPI.trim()) {
      toast.error('Name and Stripe Price ID are required')
      return
    }
    setAdding(true)
    try {
      const priceInPence = Math.round(parseFloat(newMembership.price) * 100)
      const doc = {
        name: newMembership.name.trim(),
        type: newMembership.type,
        cycle: newMembership.cycle,
        price: String(priceInPence),
        stripeAPI: newMembership.stripeAPI.trim(),
        description: newMembership.description.trim(),
        discountTitle: newMembership.discountTitle.trim() || null,
        order: memberships.length,
        enabled: false,
      }
      const ref = await firestore.collection('memberships').add(doc)
      setMemberships((prev) => [...prev, { id: ref.id, ...doc }])
      handleCloseDialog()
      toast.success(`"${doc.name}" created (disabled by default)`)
    } catch (err) {
      toast.error('Failed to create membership')
    }
    setAdding(false)
  }

  const handleToggle = async (membership) => {
    const newEnabled = membership.enabled === false ? true : false
    try {
      await firestore.collection('memberships').doc(membership.id).update({
        enabled: newEnabled,
      })
      setMemberships((prev) =>
        prev.map((m) =>
          m.id === membership.id ? { ...m, enabled: newEnabled } : m
        )
      )
      toast.success(
        `${membership.name} ${newEnabled ? 'enabled' : 'disabled'}`
      )
    } catch (err) {
      toast.error('Failed to update membership')
    }
  }

  if (error)
    return (
      <Alert severity='error'>
        <AlertTitle>Error</AlertTitle>
        {error}
      </Alert>
    )

  if (loading)
    return (
      <Box
        sx={{
          minHeight: '30vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    )

  return (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={2}
      >
        <Typography variant='body2' color='text.secondary'>
          Toggle memberships on or off to control visibility on the join page.
        </Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => setShowAddForm(true)}
        >
          Add Membership
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ background: (t) => t.palette.primary.main }}>
            <TableRow>
              <StyledCell>Name</StyledCell>
              <StyledCell>Type</StyledCell>
              <StyledCell>Price</StyledCell>
              <StyledCell>Cycle</StyledCell>
              <StyledCell align='center'>Visible on Join Page</StyledCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {memberships.map((m) => (
              <TableRow key={m.id}>
                <TableCell>
                  {editingId === m.id ? (
                    <TextField
                      size='small'
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onBlur={() => handleNameSave(m)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleNameSave(m)
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                      autoFocus
                      variant='standard'
                    />
                  ) : (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {m.name || '—'}
                      <IconButton
                        size='small'
                        onClick={() => {
                          setEditingId(m.id)
                          setEditName(m.name || '')
                        }}
                      >
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>
                  {m.type}
                </TableCell>
                <TableCell>
                  {m.price != null
                    ? `£${(Number(m.price) / 100).toFixed(2)}`
                    : '—'}
                </TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>
                  {m.cycle}
                </TableCell>
                <TableCell align='center'>
                  <Switch
                    checked={m.enabled !== false}
                    onChange={() => handleToggle(m)}
                    color='primary'
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={showAddForm}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>New Membership</DialogTitle>
        <DialogContent>
          <Box display='grid' gap={2} gridTemplateColumns='1fr 1fr' mt={1}>
            <TextField
              label='Name'
              required
              value={newMembership.name}
              onChange={(e) =>
                setNewMembership((p) => ({ ...p, name: e.target.value }))
              }
              helperText='Internal name to identify this membership'
            />
            <TextField
              label='Stripe Price ID'
              required
              placeholder='price_xxxxx'
              value={newMembership.stripeAPI}
              onChange={(e) =>
                setNewMembership((p) => ({ ...p, stripeAPI: e.target.value }))
              }
              helperText='From Stripe dashboard (Products > Pricing > Price ID)'
            />
            <TextField
              label='Price (£)'
              type='number'
              placeholder='10.00'
              value={newMembership.price}
              onChange={(e) =>
                setNewMembership((p) => ({ ...p, price: e.target.value }))
              }
              helperText='Enter in pounds (e.g. 10.00)'
            />
            <TextField
              label='Cycle'
              select
              value={newMembership.cycle}
              onChange={(e) =>
                setNewMembership((p) => ({ ...p, cycle: e.target.value }))
              }
              helperText='Billing frequency for this membership'
            >
              <MenuItem value='Weekly'>Weekly</MenuItem>
              <MenuItem value='Monthly'>Monthly</MenuItem>
              <MenuItem value='Annually'>Annually</MenuItem>
            </TextField>
            <TextField
              label='Type'
              select
              value={newMembership.type}
              onChange={(e) =>
                setNewMembership((p) => ({ ...p, type: e.target.value }))
              }
              helperText='Individual or family household membership'
              sx={{ gridColumn: '1 / -1' }}
            >
              <MenuItem value='individual'>Individual</MenuItem>
              <MenuItem value='family'>Family</MenuItem>
            </TextField>
            <TextField
              label='Discount Title'
              placeholder='e.g. NHS, police, fire, army & over 55'
              value={newMembership.discountTitle}
              onChange={(e) =>
                setNewMembership((p) => ({
                  ...p,
                  discountTitle: e.target.value,
                }))
              }
              helperText='Subtitle shown on the join page (leave blank if none)'
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label='Description'
              multiline
              maxRows={3}
              value={newMembership.description}
              onChange={(e) =>
                setNewMembership((p) => ({
                  ...p,
                  description: e.target.value,
                }))
              }
              helperText='Shown on the join page card'
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
          <Typography variant='body2' color='text.secondary' mt={2}>
            New memberships are created as disabled. Toggle them on when ready
            to go live.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleAddMembership}
            disabled={adding}
          >
            {adding ? <CircularProgress size={24} /> : 'Create Membership'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Memberships
